import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, authHeaders } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Gift, Plus, Trash2, Loader2, ChevronLeft, ChevronRight, Ticket } from "lucide-react";
import { couponBadge, normType, emptyCoupon } from "@/lib/coupon";
import CouponConfigForm from "@/components/CouponConfigForm";
import { toast } from "sonner";

const emptyTpl = emptyCoupon;
export { couponBadge };

export default function VendorCoupons() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editIdx, setEditIdx] = useState(null); // index being edited (or -1 for new)
  const [draft, setDraft] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/vendor/coupon-templates", { headers: authHeaders("vendor") });
      setTemplates((r.data.templates || []).map((t) => ({ ...t, discount_type: normType(t.discount_type) })));
    } catch (_) { toast.error("Errore nel caricamento"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const persist = async (tpls) => {
    setSaving(true);
    try {
      const r = await api.put("/vendor/coupon-templates", { templates: tpls }, { headers: authHeaders("vendor") });
      setTemplates((r.data.templates || []).map((t) => ({ ...t, discount_type: normType(t.discount_type) })));
      return true;
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore salvataggio"); return false; }
    finally { setSaving(false); }
  };

  const openNew = () => {
    if (templates.length >= 10) return toast.error("Massimo 10 coupon predefiniti");
    setEditIdx(-1); setDraft(emptyTpl());
  };
  const openEdit = (i) => { setEditIdx(i); setDraft({ ...emptyTpl(), ...templates[i], discount_type: normType(templates[i].discount_type) }); };
  const closeEditor = () => { setEditIdx(null); setDraft(null); };

  const saveDraft = async () => {
    if (!draft.title.trim()) return toast.error("Inserisci un titolo");
    const next = editIdx === -1 ? [...templates, draft] : templates.map((t, i) => (i === editIdx ? draft : t));
    const ok = await persist(next);
    if (ok) { toast.success("Coupon salvato"); closeEditor(); }
  };

  const removeTpl = async (i) => {
    const ok = await persist(templates.filter((_, idx) => idx !== i));
    if (ok) toast.success("Coupon eliminato");
  };

  return (
    <div className="space-y-5 kn-fade-up max-w-3xl" data-testid="vendor-coupons-page">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate("/vendor/followers")} data-testid="coupons-back" className="flex items-center gap-1 text-slate-500 font-semibold text-sm">
          <ChevronLeft className="h-5 w-5" /> Follower
        </button>
      </div>
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 flex items-center gap-2"><Gift className="h-6 w-6 text-[#E63946]" /> Coupon predefiniti</h1>
          <p className="text-slate-500 text-sm">Coupon pronti da allegare ai messaggi. {templates.length}/10</p>
        </div>
        <Button onClick={openNew} data-testid="coupon-add" className="h-11 rounded-xl font-bold gap-1.5 shrink-0" disabled={templates.length >= 10}>
          <Plus className="h-4 w-4" /> Aggiungi
        </Button>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#E63946]" /></div>
      ) : templates.length === 0 ? (
        <div className="kn-card p-10 text-center" data-testid="coupons-empty">
          <div className="h-14 w-14 rounded-2xl bg-[#FFE4E6] flex items-center justify-center mx-auto mb-3"><Gift className="h-7 w-7 text-[#E63946]" /></div>
          <p className="font-bold text-slate-700">Nessun coupon predefinito</p>
          <p className="text-sm text-slate-500 mt-1 mb-4">Crea il tuo primo coupon (es. sconto di benvenuto) da inviare ai clienti che ti seguono.</p>
          <Button onClick={openNew} data-testid="coupon-add-empty" className="h-11 rounded-xl font-bold gap-1.5"><Plus className="h-4 w-4" /> Crea coupon</Button>
        </div>
      ) : (
        <div className="space-y-2">
          {templates.map((t, i) => (
            <button key={t.id || i} onClick={() => openEdit(i)} data-testid={`coupon-row-${i}`}
              className="w-full kn-card p-3 flex items-center gap-3 text-left hover:ring-1 hover:ring-[#E63946]/30 transition">
              <div className="h-12 w-12 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
                {(t.images || [])[0] ? <img src={t.images[0]} alt="" className="h-full w-full object-cover" /> : <Ticket className="h-5 w-5 text-slate-400" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-slate-800 truncate">{t.title || "Coupon"}</p>
                <p className="text-xs text-slate-400 truncate">{t.description || "Nessuna descrizione"}</p>
              </div>
              <span className="px-2.5 h-7 rounded-full bg-[#FFE4E6] text-[#BE123C] font-extrabold text-xs flex items-center shrink-0" data-testid={`coupon-badge-${i}`}>{couponBadge(t)}</span>
              <ChevronRight className="h-5 w-5 text-slate-300 shrink-0" />
            </button>
          ))}
        </div>
      )}

      <Dialog open={editIdx !== null} onOpenChange={(v) => { if (!v) closeEditor(); }}>
        <DialogContent className="sm:max-w-md rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="coupon-editor">
          <DialogHeader>
            <DialogTitle className="text-xl font-extrabold">{editIdx === -1 ? "Nuovo coupon" : "Modifica coupon"}</DialogTitle>
            <DialogDescription className="text-slate-500">Imposta sconto, descrizione e immagini del coupon predefinito.</DialogDescription>
          </DialogHeader>
          {draft && (
            <div className="space-y-3.5">
              <CouponConfigForm value={draft} onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))} idPrefix="coupon" />
              <div className="flex gap-2 pt-1">
                {editIdx !== -1 && (
                  <Button type="button" variant="outline" onClick={() => { closeEditor(); removeTpl(editIdx); }} data-testid="coupon-delete"
                    className="h-12 rounded-xl text-red-500 border-red-200 hover:bg-red-50 gap-1.5 px-4"><Trash2 className="h-4 w-4" /></Button>
                )}
                <Button onClick={saveDraft} disabled={saving} data-testid="coupon-save" className="flex-1 h-12 rounded-xl font-bold gap-2">
                  {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : null} Salva coupon
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
